import { Link } from "react-router-dom";
import { ArrowRight, Bus, Car, Info, Plane } from "lucide-react";
import { useLang } from "@/i18n";
import { destinations, photos } from "@/data/site";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Reveal } from "@/components/site/Reveal";
import { CtaBanner } from "@/components/site/CtaBanner";
import { SEO } from "@/components/site/SEO";

export default function DoveSiamo() {
  const { t } = useLang();
  const how = [
    { icon: Car, ...t.dovePage.car },
    { icon: Plane, ...t.dovePage.plane },
    { icon: Bus, ...t.dovePage.bus },
  ];
  return (
    <>
      <SEO title={t.pages.dove.seo} description={t.pages.dove.desc} path="/dove-siamo" />
      <PageHero image={photos.hero} eyebrow={t.pages.dove.eyebrow} title={t.pages.dove.title} testid="dove-hero" />

      <section className="py-16 sm:py-24" data-testid="map-section">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <Reveal className="lg:col-span-3">
            <div className="overflow-hidden rounded-3xl border border-border shadow-xl">
              <iframe
                data-testid="map-iframe"
                title="Mappa — Casa Vacanze Margherita, Santa Croce Camerina"
                src="https://www.openstreetmap.org/export/embed.html?bbox=14.44%2C36.77%2C14.61%2C36.89&layer=mapnik&marker=36.8279%2C14.5267"
                className="h-[420px] w-full"
                loading="lazy"
              />
            </div>
            <p className="mt-4 flex items-start gap-2 text-sm text-ink-muted">
              <Info size={16} className="mt-0.5 shrink-0" /> {t.dovePage.quiet}
            </p>
          </Reveal>
          <div className="lg:col-span-2">
            <Section eyebrow={t.position.eyebrow} title={t.position.title} testid="dove-distances-heading" />
            <div className="space-y-2.5">
              {destinations.map((d) => (
                <div key={d.id} data-testid={`dove-distance-${d.id}`} className="flex items-center justify-between rounded-xl border border-border bg-white px-4 py-3 shadow-sm">
                  <span className="text-sm font-semibold text-ink">{t.destinations[d.id].name}</span>
                  <span className={`ml-2 shrink-0 rounded-full px-3 py-1 text-[11px] font-bold ${d.time === "0" ? "bg-terracotta text-sand" : "bg-sand-deep text-mare"}`}>
                    {d.time === "0" ? t.position.here : `${d.time} ${t.position.min}`}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-ink-muted">{t.position.note}</p>
          </div>
        </div>
      </section>

      <section className="bg-sand-deep py-16 sm:py-24" data-testid="how-to-get">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Section title={t.dovePage.howTitle} testid="how-heading" />
          <div className="grid gap-5 md:grid-cols-3">
            {how.map((h, i) => (
              <Reveal key={h.t} delay={i * 0.1}>
                <div data-testid={`how-card-${i}`} className="h-full rounded-2xl border border-border bg-white p-7 shadow-sm">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-sand-deep text-terracotta">
                    <h.icon size={22} />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-ink">{h.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-muted">{h.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <Link to="/prenota" data-testid="dove-booking-cta" className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-bold text-sand shadow-lg shadow-terracotta/25 transition-all duration-200 hover:bg-terracotta-dark hover:-translate-y-0.5">
              {t.hero.cta2} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
